/**
 * INSPECT IA TECHNICAL KNOWLEDGE BASE — base técnica CONTROLADA.
 *
 * Trechos cadastrados por quem tem autorização (documento, revisão, seção e
 * texto). O assistente só afirma o que encontrar aqui, e cita a fonte. A
 * busca é por termos (sem embeddings nesta entrega — `AIProvider.embed` é o
 * ponto de extensão) e respeita a hierarquia do comando:
 *
 *   1 documentos do projeto · 2 procedimento aplicável · 3 norma selecionada
 *   4 especificação do cliente · 5 base técnica geral
 */
export type KnowledgeSourceType = "PROJECT_DOCUMENT" | "PROCEDURE" | "NORM" | "CLIENT_SPEC" | "GENERAL";

export const KNOWLEDGE_SOURCE_LABELS: Readonly<Record<KnowledgeSourceType, string>> = {
  PROJECT_DOCUMENT: "Documento do projeto",
  PROCEDURE: "Procedimento",
  NORM: "Norma",
  CLIENT_SPEC: "Especificação do cliente",
  GENERAL: "Base técnica geral"
};

/** Menor = mais prioritário. */
const SOURCE_PRIORITY: Readonly<Record<KnowledgeSourceType, number>> = { PROJECT_DOCUMENT: 1, PROCEDURE: 2, NORM: 3, CLIENT_SPEC: 4, GENERAL: 5 };

/** Documentos sugeridos no cadastro (a base começa VAZIA — nenhum texto normativo é embutido no produto). */
export const SUGGESTED_KNOWLEDGE_DOCUMENTS = [
  "API 570", "API 574", "API 571", "API 653", "API 579", "API 580", "API 581", "ASME B31.3", "ASME VIII", "NR-13", "N-2371", "N-115"
] as const;

export interface KnowledgeEntry {
  readonly id: string;
  /** null = base geral (vale para todos os Clientes). */
  readonly clientId: string | null;
  readonly projectId: string | null;
  readonly sourceType: KnowledgeSourceType;
  readonly documentCode: string;
  readonly documentTitle: string;
  readonly revision: string;
  readonly section: string;
  readonly text: string;
  readonly tags: readonly string[];
  status: "ACTIVE" | "INACTIVE";
  readonly createdAt: string;
  readonly createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface KnowledgeContext {
  readonly clientId: string | null;
  readonly projectId: string | null;
  /** Procedimento e norma do relatório — elevam as fontes correspondentes. */
  readonly procedureCodes: readonly string[];
  readonly normCodes: readonly string[];
}

export interface KnowledgeHit {
  readonly entry: KnowledgeEntry;
  readonly score: number;
  readonly excerpt: string;
}

const STOP = new Set(["a", "o", "as", "os", "de", "da", "do", "das", "dos", "e", "é", "em", "no", "na", "nos", "nas", "um", "uma", "para", "por", "com", "que", "qual", "quais", "como", "se", "ao", "à", "este", "esta", "isso", "essa", "esse", "neste", "nesta", "sobre", "mais", "menos", "ou", "ser", "está", "pode", "deve", "meu", "minha"]);
const normalize = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
export const tokenize = (s: string) => normalize(s).split(/[^a-z0-9.]+/).filter(t => t.length > 1 && !STOP.has(t));
const compact = (s: string) => normalize(s).replace(/[^a-z0-9.]/g, "");

export function validateKnowledgeEntry(e: Partial<KnowledgeEntry>): string[] {
  const errors: string[] = [];
  if (!e.documentCode || !e.documentCode.trim()) errors.push("Informe o documento (ex.: API 570, PR-ME-01).");
  if (!e.revision || !e.revision.trim()) errors.push("Informe a revisão do documento.");
  if (!e.section || !e.section.trim()) errors.push("Informe a seção / item.");
  if (!e.text || e.text.trim().length < 20) errors.push("Cole o trecho do documento (pelo menos 20 caracteres).");
  if (!e.sourceType || !(e.sourceType in SOURCE_PRIORITY)) errors.push("Escolha o tipo de fonte.");
  if (e.sourceType === "PROJECT_DOCUMENT" && !e.projectId) errors.push("Documento do projeto exige o Projeto.");
  return errors;
}

/**
 * Busca os trechos mais relevantes, respeitando o escopo (Cliente/Projeto) e
 * a hierarquia. Um trecho só entra com pelo menos DOIS termos em comum com a
 * pergunta — um termo isolado ("corrosão") não sustenta uma resposta.
 */
export function searchKnowledge(entries: readonly KnowledgeEntry[], question: string, ctx: KnowledgeContext, limit = 3): KnowledgeHit[] {
  const q = [...new Set(tokenize(question))];
  if (q.length === 0) return [];
  const active = entries.filter(e => e.status === "ACTIVE"
    && (e.clientId === null || e.clientId === ctx.clientId)
    && (e.sourceType !== "PROJECT_DOCUMENT" || e.projectId === ctx.projectId));
  const df = new Map<string, number>();
  const docs = active.map(e => ({ e, terms: new Set(tokenize(`${e.documentCode} ${e.documentTitle} ${e.section} ${e.text} ${e.tags.join(" ")}`)) }));
  for (const d of docs) for (const t of d.terms) df.set(t, (df.get(t) ?? 0) + 1);
  const n = Math.max(1, docs.length);
  const hits: KnowledgeHit[] = [];
  for (const d of docs) {
    const common = q.filter(t => d.terms.has(t));
    if (common.length < 2) continue;
    let score = common.reduce((s, t) => s + Math.log(1 + n / (df.get(t) ?? 1)), 0);
    const code = compact(d.e.documentCode);
    if (d.e.sourceType === "PROCEDURE" && ctx.procedureCodes.some(p => compact(p).includes(code) || code.includes(compact(p)))) score *= 1.6;
    if (d.e.sourceType === "NORM" && ctx.normCodes.some(p => compact(p).includes(code) || code.includes(compact(p)))) score *= 1.4;
    score *= 1 + (6 - SOURCE_PRIORITY[d.e.sourceType]) * 0.1;
    hits.push({ entry: d.e, score, excerpt: excerptOf(d.e.text, common) });
  }
  // Hierarquia primeiro: procedimento específico nunca é trocado por conhecimento genérico.
  return hits.sort((a, b) => SOURCE_PRIORITY[a.entry.sourceType] - SOURCE_PRIORITY[b.entry.sourceType] || b.score - a.score).slice(0, limit);
}

/** Trecho de até ~320 caracteres em volta do primeiro termo encontrado — citação literal, nunca paráfrase. */
function excerptOf(text: string, terms: readonly string[]): string {
  const n = normalize(text);
  const pos = Math.min(...terms.map(t => n.indexOf(t)).filter(i => i >= 0), text.length);
  const start = Math.max(0, pos - 120);
  const slice = text.slice(start, start + 320).trim();
  return `${start > 0 ? "…" : ""}${slice}${start + 320 < text.length ? "…" : ""}`;
}
